import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  FormControlLabel,
  Checkbox,
  Link,
  Paper,
  Grid,
  CssBaseline,
  InputAdornment,
  IconButton,
  Alert,
  Avatar
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import SettingsIcon from '@mui/icons-material/Settings';
import BuildIcon from '@mui/icons-material/Build';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import BrandLogo from '../components/BrandLogo';
import { useBranding } from '../context/BrandingContext';

const Login = () => {
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const { name, subtitle, textSizing, isTitleVisible, isSubtitleVisible } = useBranding();
  const [credentials, setCredentials] = useState({ email: '', password: '' });
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCredentials({ ...credentials, [name]: value });
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('Iniciando submissão do formulário de login');
    
    if (!credentials.email || !credentials.password) {
      console.log('Campos obrigatórios não preenchidos');
      setError('Por favor, preencha todos os campos');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      console.log('Tentando fazer login com:', { email: credentials.email });
      await signIn(credentials.email, credentials.password);
      
      // Buscar dados do usuário do localStorage para diagnóstico
      const userData = JSON.parse(localStorage.getItem('@SenhasApp:user'));
      console.log('Login bem-sucedido, Detalhes do usuário:', {
        id: userData?.id,
        nome: userData?.nome,
        cargo: userData?.cargo,
        cargoTipo: typeof userData?.cargo,
        email: userData?.email
      });
      
      console.log('Redirecionando para /home');
      navigate('/home');
    } catch (err) {
      console.error('Erro detalhado ao realizar login:', err);
      setError(err.response?.data?.message || 'Credenciais inválidas');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Grid container component="main" sx={{ height: '100vh', padding: 0 }}>
      <CssBaseline />

      <Box sx={{ position: 'absolute', top: 50, left: 50, display: 'flex', flexDirection: 'column' }}>
        <BrandLogo slot="login" alt="Logo" sx={{ width: '400px' }} />
        {/* Textos de branding condicionais */}
        {isTitleVisible('login') && name && (
          <Typography
            variant="h4"
            component="div"
            sx={{ mt: 1, fontWeight: 700, color: '#ffffff', textShadow: '0 1px 2px rgba(0,0,0,0.25)', fontSize: `${textSizing?.login?.titlePx || 28}px` }}
          >
            {name}
          </Typography>
        )}
        {isSubtitleVisible('login') && subtitle && (
          <Typography
            variant="subtitle1"
            component="div"
            sx={{ mt: 0.5, color: '#ffffff', opacity: 0.9, textShadow: '0 1px 2px rgba(0,0,0,0.2)', fontSize: `${textSizing?.login?.subtitlePx || 16}px` }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>
      
      {/* Lado esquerdo - Informações (70%) */}
      <Grid
        item
        xs={false}
        sm={8}
        md={8}
        lg={9}
        sx={{
          bgcolor: '#0a1929',
          color: 'white',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          p: { xs: 3, md: 6 }
        }}
      >
        <Box sx={{ ml: 10, maxWidth: 900 }}>
          <Typography variant="h4" component="h1" sx={{ mb: 8, fontWeight: 600, fontSize: '2.4rem', textAlign: 'left' }}>
            Sistema de Gerenciamento de Senhas
          </Typography>

          <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
            <SettingsIcon color="inherit" />
            <Box>
              <Typography variant="h6" component="h2" sx={{ mb: 0.5, textAlign: 'left' }}>
                Organização Eficiente
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.7, textAlign: 'left' }}>
                Nosso sistema organiza a fila de atendimento do cartório, reduzindo tempo de espera 
                e otimizando o fluxo de pessoas.
              </Typography>
            </Box>
          </Box>

          <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
            <BuildIcon color="inherit" />
            <Box>
              <Typography variant="h6" component="h2" sx={{ mb: 0.5, textAlign: 'left' }}>
                Estrutura Confiável
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.7, textAlign: 'left' }}>
                Sistema robusto para gerenciamento de senhas com alta disponibilidade, 
                sem interrupções mesmo nos dias de maior movimento.
              </Typography>
            </Box>
          </Box>

          <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
            <ThumbUpIcon color="inherit" />
            <Box>
              <Typography variant="h6" component="h2" sx={{ mb: 0.5, textAlign: 'left' }}>
                Atendimento Facilitado
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.7, textAlign: 'left' }}>
                Interface intuitiva para funcionários, com painéis claros e 
                notificações sonoras para melhor experiência de atendimento.
              </Typography>
            </Box>
          </Box>

          <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
            <NewReleasesIcon color="inherit" />
            <Box>
              <Typography variant="h6" component="h2" sx={{ mb: 0.5, textAlign: 'left' }}>
                Recursos Modernos
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.7, textAlign: 'left' }}>
                Categorização de serviços, prioridades legais e estatísticas 
                em tempo real para uma gestão eficaz do atendimento.
              </Typography>
            </Box>
          </Box>
        </Box>
      </Grid>

      {/* Lado direito - Formulário de login (30%) */}
      <Grid 
        item 
        xs={12} 
        sm={4} 
        md={4} 
        lg={3} 
        component={Paper} 
        elevation={6} 
        square
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center'
        }}
      >
        <Box
          sx={{
            mx: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Avatar sx={{ m: 1, bgcolor: 'primary.main' }}>
            <LockOutlinedIcon />
          </Avatar>
          
          <Typography component="h1" variant="h5" sx={{ mb: 4 }}>
            Entrar
          </Typography>

          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 3 }}>
              {error}
            </Alert>
          )}

          <Box component="form" noValidate onSubmit={handleSubmit} sx={{ width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email"
              name="email"
              autoComplete="email"
              autoFocus
              value={credentials.email}
              onChange={handleInputChange}
            />
            
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Senha"
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="current-password"
              value={credentials.password}
              onChange={handleInputChange}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox 
                    value="remember" 
                    color="primary" 
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                  />
                }
                label="Lembrar-me"
              />
              <Link href="#" variant="body2">
                Esqueceu sua senha?
              </Link>
            </Box>
            
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Entrando...' : 'Entrar'}
            </Button>
            
            <Box mt={5} textAlign="center">
              <Typography variant="body2" color="text.secondary">
                © {new Date().getFullYear()} Sistema de Gerenciamento de Senhas
              </Typography>
            </Box>
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
};

export default Login; 